import React, {
  useCallback,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import {
  getProject,
  useProject,
  useUpdateProject,
} from '@client/store/project';
import {
  IUndoRedoState,
  getUndoState,
  updateUndoRedoStates,
  getRedoState,
  UndoRedoActionType,
  IUndoRedoInputState,
  subscribeToUndoRedoChanges,
  setCurrentEnrichedUndoRedoState,
  isUndoRedoSuppressedForInFlightPersist,
  createUndoRedoHistoryCheckpoint,
  isUndoRedoHistoryCheckpointCurrent,
  restoreUndoRedoHistoryCheckpoint,
} from '@shared/helpers/undo-redo.helpers';
import {
  getSelectedVersionId,
  useSelectedVersionId,
  useUIState,
} from '@client/store/ui';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { useProjectNavigation } from './use-router.hooks';
import { Project } from '@shared/models/project.model';
import { getBuildingVersionById } from '@shared/helpers/recursive-element.helpers';
import { OptionsObject } from 'notistack';
import { useSnackbar } from './use-snackbar.hooks';
import { useExpandParents } from './use-expand-elements.hooks';
import { usePrevious } from './use-utils.hooks';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  composeRedoneMessage,
  composeUndoneMessage,
} from '@shared/helpers/undo-redo-message.helpers';
import { Button } from '@ui/buttons';
import { PROPOSAL_SEARCH_PARAM } from './use-selected-proposal-id.hooks';
import { getRememberedProposalForVersion } from './use-router-query-params.hooks';

const UNDO_SNACKBAR_OPTIONS: OptionsObject = {
  autoHideDuration: 2000,
  preventDuplicate: true,
};

const TEMPORAL_ERROR_SNACKBAR_OPTIONS: OptionsObject = {
  ...UNDO_SNACKBAR_OPTIONS,
  variant: 'error',
};

type ApplyTemporalStateResult = {
  project: Project;
  applied: boolean;
};

export const useInitUndoRedo = (): void => {
  const project = useProject();
  const selectedVersionId = useSelectedVersionId();
  const nextState: IUndoRedoInputState | undefined = useMemo(
    () => (project.enriched ? { project, selectedVersionId } : undefined),
    [project, selectedVersionId],
  );
  const prevState = usePrevious(nextState, {
    when: guards.is.defined,
  });

  useEffect(() => {
    if (nextState)
      setCurrentEnrichedUndoRedoState(
        nextState.project,
        nextState.selectedVersionId,
      );
    if (prevState && nextState) updateUndoRedoStates(prevState, nextState);
  }, [prevState, nextState]);
};

/**
 * Used for both undo and redo
 */
const useApplyTemporalState = () => {
  const updateProject = useUpdateProject();
  const { setRecentlyUndoRedoElementId } = useUIState(
    'setRecentlyUndoRedoElementId',
  );
  const { enqueueSnackbar } = useSnackbar();
  const expandParents = useExpandParents();
  const navigateTo = useProjectNavigation();

  return useCallback(
    async (
      state: IUndoRedoState | undefined,
      type: UndoRedoActionType,
    ): Promise<ApplyTemporalStateResult> => {
      const currentProject = getProject();
      // Nothing to undo/redo. Stay silent while persist still owns the latest frame.
      if (!state) {
        if (isUndoRedoSuppressedForInFlightPersist())
          return { project: currentProject, applied: false };
        enqueueSnackbar(`Nothing to ${type}`, UNDO_SNACKBAR_OPTIONS);
        return { project: currentProject, applied: false };
      }

      const { project, changedElementId, selectedVersionId } = state;
      const historyCheckpoint = createUndoRedoHistoryCheckpoint();
      const temporalProject = {
        ...project,
        // Backend rejects if updated_at is older than the current project to prevent invalid race conditions so we use the current project's updated_at.
        // If we're behind we should still get rejected by the backend.
        updated_at: currentProject.updated_at,
      };
      // Stack entries are normally enriched commits ({@link Project.enriched} === true).
      // Optimistic / in-flight snapshots use `enriched: false`; redo/undo onto those must
      // run the same enrich path as {@link useUpdateProject} or CO₂/expressions stay stale.
      const snapshotIsEnriched = project.enriched === true;
      const updatedProject = await updateProject({
        kind: 'snapshot',
        project: temporalProject,
        // Restore keeps the snapshot's own labels — the stamp point in
        // useUpdateProject clears them unless forwarded.
        effectsOutsideUndo: project.effectsOutsideUndo,
        disableErrorSnackbar: true,
        ...(snapshotIsEnriched ? { skipEnrichment: true } : {}),
      });

      // Persist rewrites snapshots (enrichment, blueprint rasters). Error handlers
      // return the pre-update store project; 422 recovery resets history instead.
      const applied =
        !Object.is(updatedProject, currentProject) &&
        isUndoRedoHistoryCheckpointCurrent(historyCheckpoint);
      if (!applied) {
        restoreUndoRedoHistoryCheckpoint(
          historyCheckpoint,
          temporalProject,
          updatedProject,
        );
        return { project: updatedProject, applied: false };
      }

      // History spans building versions, so a snapshot may belong to one the user left.
      // Follow it back, otherwise the restore silently edits a version that is off screen.
      if (
        selectedVersionId &&
        selectedVersionId !== getSelectedVersionId() &&
        getBuildingVersionById(updatedProject, selectedVersionId)
      ) {
        const destinationVersion = getBuildingVersionById(
          updatedProject,
          selectedVersionId,
        );
        const destinationProposalId = getRememberedProposalForVersion(
          updatedProject.id,
          selectedVersionId,
          destinationVersion?.proposals[0]?.id,
          destinationVersion?.proposals.map(({ id }) => id),
        );
        navigateTo(
          {
            versionId: selectedVersionId,
            elementId: URL_CATEGORY_NONE,
          },
          {
            searchParams: {
              [PROPOSAL_SEARCH_PARAM]: destinationProposalId ?? null,
            },
          },
        );
      }

      const highlightTarget = changedElementId?.startsWith('proposal:')
        ? undefined
        : changedElementId;
      setRecentlyUndoRedoElementId(highlightTarget);
      expandParents(highlightTarget);

      return { project: updatedProject, applied: true };
    },
    [
      enqueueSnackbar,
      expandParents,
      navigateTo,
      setRecentlyUndoRedoElementId,
      updateProject,
    ],
  );
};

export const useUndo = () => {
  const applyTemporalState = useApplyTemporalState();
  const { enqueueSnackbar } = useSnackbar();
  const redo = useRedo();

  return useCallback(async () => {
    const snapshotBeforeUndo = getProject();
    const state = getUndoState(snapshotBeforeUndo);
    const { project: result, applied } = await applyTemporalState(
      state,
      'undo',
    );
    if (state && applied) {
      const message = composeUndoneMessage(
        snapshotBeforeUndo.effectsOutsideUndo,
      );
      enqueueSnackbar(message, {
        ...UNDO_SNACKBAR_OPTIONS,
        action: () =>
          React.createElement(Button, {
            variant: 'text',
            size: 'small',
            onClick: redo,
            children: 'Redo',
          }),
      });
    } else if (state) {
      enqueueSnackbar('Could not undo change', TEMPORAL_ERROR_SNACKBAR_OPTIONS);
    }
    return result;
  }, [applyTemporalState, enqueueSnackbar, redo]);
};

export const useRedo = () => {
  const applyTemporalState = useApplyTemporalState();
  const { enqueueSnackbar } = useSnackbar();

  return useCallback(async () => {
    const state = getRedoState();
    const { project: result, applied } = await applyTemporalState(
      state,
      'redo',
    );
    if (state && applied) {
      const message = composeRedoneMessage();
      enqueueSnackbar(message, UNDO_SNACKBAR_OPTIONS);
    } else if (state) {
      enqueueSnackbar('Could not redo change', TEMPORAL_ERROR_SNACKBAR_OPTIONS);
    }
    return result;
  }, [applyTemporalState, enqueueSnackbar]);
};

const UNDO_STACK_READY_MAX_FRAMES = 90;

/**
 * Resolves after {@link getUndoState} is set for the current project, or after a rAF budget.
 * {@link useInitUndoRedo} fills the stack in a `useEffect` after enriched commits.
 */
export const useWaitForUndoStack = (): (() => Promise<void>) => {
  return useCallback(async () => {
    for (let i = 0; i < UNDO_STACK_READY_MAX_FRAMES; i++) {
      if (getUndoState(getProject())) return;
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    }
  }, []);
};

const getCanUndo = () => Boolean(getUndoState(getProject()));
const getCanRedo = () => Boolean(getRedoState());

let undoRedoHotkeysActive = false;
const undoRedoHotkeysActiveListeners = new Set<() => void>();

const subscribeToUndoRedoHotkeysActive = (onStoreChange: () => void) => {
  undoRedoHotkeysActiveListeners.add(onStoreChange);
  return () => {
    undoRedoHotkeysActiveListeners.delete(onStoreChange);
  };
};

const getUndoRedoHotkeysActive = () => undoRedoHotkeysActive;

/** Set by {@link useGlobalKeyListener} when document undo/redo shortcuts are attached. */
export const setUndoRedoHotkeysActive = (active: boolean): void => {
  if (undoRedoHotkeysActive === active) return;
  undoRedoHotkeysActive = active;
  undoRedoHotkeysActiveListeners.forEach((listener) => listener());
};

/** True while global undo/redo hotkeys are registered (mirrors listener attach state). */
export const useUndoRedoHotkeysActive = (): boolean =>
  useSyncExternalStore(
    subscribeToUndoRedoHotkeysActive,
    getUndoRedoHotkeysActive,
  );

/** Reactively returns whether undo is available, driven by the undo/redo stack directly. */
export const useCanUndo = (): boolean =>
  useSyncExternalStore(subscribeToUndoRedoChanges, getCanUndo);

/** Reactively returns whether redo is available, driven by the undo/redo stack directly. */
export const useCanRedo = (): boolean =>
  useSyncExternalStore(subscribeToUndoRedoChanges, getCanRedo);
